<script setup lang="ts">
  import { computed, getCurrentInstance } from 'vue';
  import type { ICatalogPriceView } from '@/domain/catalog/catalog-view';

  /**
   * The row's price as a quiet field (plan §5.8): a tap opens «Cambiar
   * precio» at every width. «$ 9.800 a $ 13.900» for sizes, the old price
   * struck through after a discount, «Sin precio» when it costs 0.
   */
  const props = defineProps<{
    price: ICatalogPriceView;
    /** The product name, for the accessible label. */
    name: string;
    disabled?: boolean;
    /** Why it is disabled («Sin conexión»). */
    disabledTitle?: string;
  }>();

  const emit = defineEmits<{ (e: 'click', event: MouseEvent): void }>();

  const proxy = getCurrentInstance()!.proxy;

  const text = computed(() => {
    if (!props.price.text) return String(proxy.$t('menuPage.row.noPrice'));
    return props.price.highText
      ? String(
          proxy.$t('menuPage.row.priceRange', {
            low: props.price.text,
            high: props.price.highText,
          })
        )
      : props.price.text;
  });
</script>

<template>
  <button
    type="button"
    class="v2-catalog-price-field"
    :class="`tone-${price.tone}`"
    :disabled="disabled"
    :title="disabled ? disabledTitle : undefined"
    :aria-label="$t('menuPage.row.changePrice', { name, price: text })"
    @click.stop="emit('click', $event)"
  >
    <span class="price-value">{{ text }}</span>
    <s v-if="price.oldText" class="price-old">{{ price.oldText }}</s>
  </button>
</template>

<style scoped lang="scss">
  .v2-catalog-price-field {
    // 36 px visual; the ::after stretches the hit area to 44 px.
    @apply tw-relative tw-inline-flex tw-h-9 tw-max-w-full tw-flex-none tw-cursor-pointer tw-items-center tw-whitespace-nowrap tw-rounded-lg tw-border tw-border-solid tw-border-neutral-200 tw-bg-surface tw-px-2.5 tw-text-left tw-transition-colors tw-duration-150;

    &::after {
      @apply tw-absolute tw-left-0 tw-right-0;
      content: '';
      top: -4px;
      bottom: -4px;
    }

    &:hover:not(:disabled) {
      @apply tw-border-neutral-300 tw-bg-neutral-50;
    }

    &:focus-visible {
      @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
    }

    &:disabled {
      @apply tw-cursor-default;
    }

    .price-value {
      @apply tw-truncate tw-font-semibold tw-tabular-nums tw-text-neutral-900;
      font-size: 14.5px;
    }

    .price-old {
      @apply tw-ml-1.5 tw-text-xs tw-tabular-nums tw-text-neutral-400;
    }

    &.tone-missing .price-value {
      @apply tw-font-medium tw-text-amber-700;
    }

    &.tone-missingNeutral .price-value {
      @apply tw-font-medium tw-text-neutral-500;
    }
  }
</style>
